import { inject, Injectable } from '@angular/core'
import { HttpParams, HttpResponse } from '@angular/common/http'
import { Observable } from 'rxjs'
import { MediaHubApiService } from './media-hub-api.service'
import { Playlist } from '@models/playlist.model'

export interface CreatePlaylistRequest {
  name: string
  description?: string
  thumbnailUrl?: string
  tags?: string[]
  projectId?: string
  mediaAssetChatId?: string
  generalChatId?: string
  mediaAssetIds?: string[]
  mediaLibraryAssetIds?: string[]
}

export interface UpdatePlaylistRequest {
  name?: string
  description?: string
  thumbnailUrl?: string
  tags?: string[]
}

export interface SavePlaylistAssetRequest {
  mediaAssetIds: string[]
}

export interface AddMediaLibraryAssetsRequest {
  mediaLibraryAssetIds: string[]
}

export interface AddMediaLibraryAssetsResponse {
  success: boolean
  message: string
}

export interface PlaylistResponse {
  success?: boolean
  message?: string
  playlist: {
    id: string
    projectId?: string
    userId: string
    name: string
    description?: string
    thumbnailUrl?: string
    tags?: string[]
    mediaAssetIds: string[]
    createdAt: string
    updatedAt: string
  }
}

export interface PlaylistsResponse {
  success?: boolean
  count?: number
  playlists: Playlist[]
}

export interface GetPlaylistsOptions {
  tags?: string[]
}

export interface DeletePlaylistResponse {
  success: boolean
  message: string
}

@Injectable({
  providedIn: 'root',
})
export class PlaylistApiService {
  private apiService = inject(MediaHubApiService)

  createPlaylist$(request: CreatePlaylistRequest): Observable<PlaylistResponse> {
    return this.apiService.postBaseRx<CreatePlaylistRequest, PlaylistResponse>('/playlists', request)
  }

  getAllPlaylists$(): Observable<PlaylistsResponse> {
    return this.apiService.getBaseRx<PlaylistsResponse>('/playlists')
  }

  getPlaylists$(options?: GetPlaylistsOptions): Observable<PlaylistsResponse> {
    let params = new HttpParams()

    if (options?.tags && options.tags.length > 0) {
      params = params.set('tags', options.tags.join(','))
    }

    return this.apiService.getBaseRx<PlaylistsResponse>('/playlists', params)
  }

  getPlaylistsByTags$(tags: string[]): Observable<PlaylistsResponse> {
    return this.getPlaylists$({ tags })
  }

  getPlaylistById$(playlistId: string): Observable<PlaylistResponse> {
    return this.apiService.getBaseRx<PlaylistResponse>(`/playlists/${playlistId}`)
  }

  updatePlaylist$(playlistId: string, request: UpdatePlaylistRequest): Observable<PlaylistResponse> {
    return this.apiService.putBaseRx<UpdatePlaylistRequest, PlaylistResponse>(`/playlists/${playlistId}`, request)
  }

  savePlaylistAssets$(playlistId: string, assetIds: string[]): Observable<PlaylistResponse> {
    const request: SavePlaylistAssetRequest = { mediaAssetIds: assetIds }
    return this.apiService.putBaseRx<SavePlaylistAssetRequest, PlaylistResponse>(
      `/playlists/${playlistId}/assets`,
      request
    )
  }

  deletePlaylist$(playlistId: string): Observable<DeletePlaylistResponse> {
    return this.apiService.deleteBaseRx<DeletePlaylistResponse>(`/playlists/${playlistId}`)
  }

  addMediaLibraryAssets$(playlistId: string, mediaLibraryAssetIds: string[]): Observable<AddMediaLibraryAssetsResponse> {
    const request: AddMediaLibraryAssetsRequest = { mediaLibraryAssetIds }
    return this.apiService.postBaseRx<AddMediaLibraryAssetsRequest, AddMediaLibraryAssetsResponse>(
      `/playlists/${playlistId}/media-library-assets`,
      request
    )
  }

  // Returns the raw FCP7 xmeml document. Errors come back as JSON but are still
  // delivered as a Blob because the request uses responseType 'blob'.
  exportPlaylistAsPremiereXml(playlistId: string): Promise<HttpResponse<Blob>> {
    return this.apiService.getBaseBlobResponse(`/playlists/${playlistId}/export/premiere-xml`, 'application/xml')
  }
}
